import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { makeStyles } from '@material-ui/core';

import { images as imageApi, resetPasswordConfirm2 } from '../Apis/apis';
import { PASSWORD_REQUIREMENT_MESSAGE, passwordRegValidator } from '../CommonFunction/validators';
import OnOffByScroll from '../Animation/onOffByScroll';
import StandardDialog from '../Dialog/standardDialog';
import various from '../General/various';
import Footer from '../Footer/footer';
import { ogpGenerator } from '../ogp';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '500px',
  },
  formWrapper: {
    marginTop: '168px',
    '& > p': {
      textAlign: 'start',
    },
  },

  titles: {},
  inputs: {},
  error: {
    color: 'red',
    fontSize: '0.7em',
  },
  formcontainer: {
    marginTop: '32px',
    width: '100%',
    '& > form': {
      '& > div': {
        width: '100%',
        display: 'flex',
        margin: '0 auto',
        marginBottom: '32px',

        '& > div': {
          textAlign: 'start',
          display: 'flex',
          justifyContent: 'start',
          '& > img': {
            maxWidth: '300px',
            maxHeight: '300px',
            objectFit: 'contain',
          },
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
        },

        '& $titles': {
          width: '20%',
          marginRight: '4px',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > span': {
            color: 'red',
          },
        },

        '& $inputs': {
          width: '80%',
          flexWrap: 'wrap',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > input': {
            width: '100%',
            height: '40px',
            padding: '8px',
            fontFamily: theme.typography.fontFamily,
            border: '0.5px solid grey',
            borderRadius: '0.5em',
            boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.1) inset',
            '&:focus': {
              outline: `1.2px ${theme.palette.primary.main} outset`,
            },
          },
        },

        '@media screen and (max-width:750px)': {
          flexWrap: 'wrap',
        },
      },
    },
  },
  button: {
    width: '85% !important',
    margin: '32px 0px 0px 0px !important',
    justifyContent: 'end',
    '& > button': {
      display: 'block',
      fontFamily: theme.typography.fontFamily,
      fontWeight: 'bold',
      borderRadius: '0.5em',
      border: 'none',
      padding: '4px 8px',
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0px 0px 15px rgba(0,0,0,0.1)',
      color: '#fff',
      cursor: 'pointer',
    },
  },
}));

const uncertainOutcome = '変更が完了したか確認できていません。続けて送信せず、ログイン状況を確認してください。';
const failureMessages = {
  missing_token: '再設定リンクに必要な情報がありません。メール内のリンクを開き直してください。',
  expired_token: '再設定リンクの有効期限が切れています。新しいリンクを取得してください。',
  invalid_token: '再設定リンクが無効です。メール内のリンクを確認するか、新しいリンクを取得してください。',
  change_rejected: 'この入力へのパスワード変更は受け付けられませんでした。別のパスワードを指定してください。',
  business_failure: 'パスワード変更を受け付けられませんでした。再設定リンクを確認してください。',
  timeout: `通信が時間内に完了しませんでした。${uncertainOutcome}`,
  network_failure: `通信を完了できませんでした。${uncertainOutcome}`,
  http_failure: `サーバーの応答を確認できませんでした。${uncertainOutcome}`,
  invalid_response: `処理結果の応答を確認できませんでした。${uncertainOutcome}`,
  canceled: `通信が中断されました。${uncertainOutcome}`,
  unknown: `処理結果を確認できませんでした。${uncertainOutcome}`,
};

const ForgotPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const [image, setImage] = useState();
  const [openFor, setOpenFor] = useState(null);
  const [pendingFor, setPendingFor] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const requestScope = useRef(null);
  const failureRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();

  // OGP設定
  const ogp = useCallback((props) => {
    ogpGenerator(props);
  }, []);

  useLayoutEffect(() => {
    if (image) {
      const ogpProps = {
        page: 'forgot_confirm',
        pageTitle: '刀禪OfficialWebSiteパスワード再設定完了ページ',
        path: location.pathname,
        imageUrl: image.OfficialImage_TouzenLogoBlack.url,
      };
      ogp(ogpProps);
    }
  }, [image, location, ogp]);
  // OGP設定ここまで

  useEffect(() => {
    imageApi(setImage);
  }, []);

  const query = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location]);
  const token = query.get('token');
  const pageKey = `${location.pathname}:${location.search}`;
  const open = openFor === pageKey;
  const sending = pendingFor === pageKey;
  const failureReason = !token ? 'missing_token' : outcome?.pageKey === pageKey ? outcome.reason : null;

  useEffect(() => {
    const scope = { pageKey, active: true, sending: false, controller: new AbortController() };
    requestScope.current = scope;
    setPendingFor(null);
    setOpenFor(null);
    setOutcome(null);
    return () => { scope.active = false; scope.controller.abort(); };
  }, [pageKey]);

  useEffect(() => {
    if (failureReason) failureRef.current?.focus();
  }, [failureReason, pageKey]);

  const onSubmit = handleSubmit(async (data) => {
    const scope = requestScope.current;
    if (!scope?.active || scope.pageKey !== pageKey || scope.sending) return;
    if (!token) { setOutcome({ pageKey, reason: 'missing_token' }); return; }
    scope.sending = true;
    setPendingFor(pageKey);
    setOpenFor(null);
    setOutcome(null);
    try {
      const result = await resetPasswordConfirm2(token, data.password, { signal: scope.controller.signal });
      if (!scope.active || requestScope.current !== scope) return;
      if (result?.ok === true && result.reason === 'success') setOpenFor(pageKey);
      else setOutcome({ pageKey, reason: Object.prototype.hasOwnProperty.call(failureMessages, result?.reason) ? result.reason : 'unknown' });
    } catch (error) {
      if (scope.active && requestScope.current === scope) setOutcome({ pageKey, reason: 'unknown' });
    } finally {
      scope.sending = false;
      if (scope.active && requestScope.current === scope) setPendingFor(null);
    }
  });

  const handleClose = () => {
    setOpenFor(null);
  };
  const toNavigate = () => {
    return navigate(various.general.home);
  };

  return (
    <>
      <div className={classes.container}>
        <div>
          <OnOffByScroll place="General" />
        </div>
        <div className={classes.formWrapper}>
          <p>設定するパスワードを送信してください。</p>
          <p>{PASSWORD_REQUIREMENT_MESSAGE}</p>
          {failureReason && <div role="alert" tabIndex={-1} ref={failureRef} className={classes.error}>{failureMessages[failureReason]}</div>}
          <div className={classes.formcontainer}>
            <form
              method="POST"
              encType="multipart/form-data"
              onSubmit={onSubmit}
              aria-busy={sending}
            >
              <div>
                <div className={classes.titles}>
                  <label htmlFor="password">新しいパスワード</label>
                  <span> *</span>
                </div>
                <div className={classes.inputs}>
                  <input
                    id="password"
                    type="password"
                    disabled={sending || open}
                    autoComplete="new-password"
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    {...register('password', {
                      required: { value: true, message: '入力必須の項目です' },
                      validate: {
                        message: (v) =>
                          passwordRegValidator(v) || PASSWORD_REQUIREMENT_MESSAGE,
                      },
                    })}
                  />
                  {errors.password?.message && (
                    <div id="password-error" role="alert" className={classes.error}>
                      {errors.password.message}
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.button}>
                <button type="submit" disabled={sending || open || !token}>{sending ? '送信中…' : '送信'}</button>
              </div>
              {sending && <p role="status">再設定の結果を確認しています。</p>}
            </form>
          </div>
        </div>
        <StandardDialog
          open={open}
          handleClose={handleClose}
          text={{
            title: 'パスワードを設定しました。',
            content:
              '返信メール等はありません。パスワードは大切に保管してください。',
            agree: '了解',
            disagree: null,
          }}
          func={{ agreeFunc: toNavigate, disagreeFunc: null }}
        />
      </div>
      <Footer />
    </>
  );
};
export default ForgotPassword;
